import jsPDF from 'jspdf';

import type { EligibleTraining } from '@/data/eligibleTrainings';

type TrainingData = EligibleTraining;

// Gaius logo as base64 loaded once
let logoBase64: string | null = null;
const loadLogo = (): Promise<string> => {
  if (logoBase64) return Promise.resolve(logoBase64);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      logoBase64 = canvas.toDataURL('image/png');
      resolve(logoBase64);
    };
    img.onerror = () => resolve('');
    img.src = '/gaius-logo.webp';
  });
};

export async function generateTrainingPDF(training: TrainingData, lang: 'fr' | 'en') {
  const fr = lang === 'fr';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  const logoData = await loadLogo();

  const navy = [30, 41, 59] as const;
  const slate = [51, 65, 85] as const;
  const muted = [100, 116, 139] as const;
  const accent = [59, 130, 246] as const;

  // Add footer on every page
  const addPageFooter = () => {
    const footerY = pageHeight - 12;
    doc.setDrawColor(203, 213, 225);
    doc.line(margin, footerY - 4, pageWidth - margin, footerY - 4);
    doc.setFontSize(6.5);
    doc.setTextColor(...muted);
    doc.setFont('helvetica', 'normal');
    doc.text('Gaius — Formation IA Juridique — gaius-tech.com — formation@gaius-tech.com', margin, footerY);
    doc.text(fr ? 'Organisme certifié Qualiopi' : 'Qualiopi-certified organization', pageWidth - margin, footerY, { align: 'right' });
  };

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - 20) {
      addPageFooter();
      doc.addPage();
      y = 20;
    }
  };

  // ── Header ──
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 44, 'F');

  // Logo image
  if (logoData) {
    try {
      doc.addImage(logoData, 'PNG', margin, 6, 12, 12);
    } catch { /* fallback to text */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('GAIUS', margin + (logoData ? 15 : 0), 14);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setCharSpace(0);
  doc.text(fr ? 'Formation IA Juridique' : 'Legal AI Training', margin + (logoData ? 15 : 0), 19);

  // Badges
  doc.setFontSize(7);
  doc.setTextColor(180, 200, 255);
  doc.text('Qualiopi · OPCO · FIF-PL · CPF', pageWidth - margin, 14, { align: 'right' });
  doc.text('gaius-tech.com', pageWidth - margin, 19, { align: 'right' });

  // Title in header
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setCharSpace(0);
  const titleLines = doc.splitTextToSize(training.title, contentWidth);
  doc.text(titleLines, margin, 30);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setCharSpace(0);
  doc.text(doc.splitTextToSize(training.subtitle, contentWidth), margin, 38);
  y = 52;

  // Reset
  doc.setTextColor(...navy);
  doc.setCharSpace(0);

  // ── Meta info ──
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.setFont('helvetica', 'normal');
  doc.setCharSpace(0);
  const metaItems = [
    `${fr ? 'Niveau' : 'Level'}: ${training.level}`,
    `${fr ? 'Public' : 'Audience'}: ${training.audience}`,
     `${fr ? 'Format' : 'Format'}: ${fr ? 'Distanciel (webinaire 7h avec pauses et pause-déjeuner)' : 'Remote (7h webinar with breaks and lunch break)'}`,
     `${fr ? 'Tarif' : 'Price'}: ${training.priceRemote}`,
    `${fr ? 'Code formation' : 'Training code'}: ${training.rncp.codeFormation}`,
  ];
  metaItems.forEach(item => {
    doc.text(item, margin, y);
    y += 4.5;
  });
  y += 3;

  // ── Funding badges ──
  doc.setFillColor(...accent);
  ['OPCO', 'FIF-PL', 'CPF', 'Qualiopi'].forEach((badge, i) => {
    const bx = margin + i * 25;
    doc.roundedRect(bx, y, 22, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setCharSpace(0);
    doc.text(badge, bx + 11, y + 4.2, { align: 'center' });
  });
  doc.setTextColor(...navy);
  doc.setFont('helvetica', 'normal');
  y += 12;

  // ── Section helper ──
  const addSection = (title: string) => {
    checkPage(12);
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, contentWidth, 8, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...navy);
    doc.setCharSpace(0);
    doc.text(title.toUpperCase(), margin + 3, y + 5.5);
    doc.setFont('helvetica', 'normal');
    y += 12;
  };

  // ── Body text helper ──
  const bodyText = (text: string, indent = 2) => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...slate);
    doc.setCharSpace(0);
    const lines = doc.splitTextToSize(text, contentWidth - indent - 2);
    doc.text(lines, margin + indent, y);
    y += lines.length * 4.2 + 1.5;
  };

  // ── Introduction ──
  if (training.introduction.length > 0) {
    addSection(fr ? 'Présentation' : 'Overview');
    training.introduction.forEach(para => {
      checkPage(15);
      bodyText(para);
    });
    y += 2;
  }

  // ── Objectives ──
  addSection(fr ? 'Objectifs pédagogiques' : 'Learning Objectives');
  doc.setFontSize(8.5);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);
  training.objectives.forEach(obj => {
    checkPage(7);
    const lines = doc.splitTextToSize(`-  ${obj}`, contentWidth - 4);
    doc.text(lines, margin + 2, y);
    y += lines.length * 4.2 + 1.5;
  });
  y += 3;

  // ── Program ──
  addSection(fr ? 'Programme détaillé' : 'Detailed Program');
  training.program.forEach((section, i) => {
    checkPage(10);
    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...navy);
    doc.setCharSpace(0);
    doc.text(`${i + 1}. ${section.title}`, margin + 2, y);
    doc.setFont('helvetica', 'normal');
    y += 5.5;
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    doc.setCharSpace(0);
    section.items.forEach(item => {
      checkPage(6);
      const lines = doc.splitTextToSize(`-  ${item}`, contentWidth - 10);
      doc.text(lines, margin + 6, y);
      y += lines.length * 4.2;
    });
    y += 3;
  });

  // ── Expected Results ──
  addSection(fr ? 'Résultats attendus' : 'Expected Results');
  doc.setFontSize(8.5);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);
  training.expectedResults.forEach(r => {
    checkPage(7);
    doc.setFont('helvetica', 'bold');
    doc.text(`${r.metric}`, margin + 2, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`  ${r.label}`, margin + 18, y);
    y += 5.5;
  });
  y += 3;

  // ── Professional Outcomes ──
  addSection(fr ? 'Débouchés professionnels' : 'Professional Outcomes');
  training.professionalOutcomes.forEach(outcome => {
    checkPage(7);
    bodyText(`>  ${outcome}`);
  });
  y += 2;

  // ── Attestation ──
  addSection(fr ? 'Attestation délivrée' : 'Certificate Issued');
  bodyText(training.attestation);
  y += 2;

  // ── Prerequisites ──
  addSection(fr ? 'Prérequis' : 'Prerequisites');
  bodyText(training.prerequisites);
  y += 2;

  // ── Pedagogy ──
  addSection(fr ? 'Méthodes pédagogiques' : 'Teaching Methods');
  doc.setFontSize(8.5);
  doc.setCharSpace(0);
  training.pedagogy.forEach(p => {
    checkPage(6);
    bodyText(`-  ${p}`);
  });

  // ── Evaluation ──
  checkPage(10);
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  doc.setCharSpace(0);
  doc.text(`${fr ? 'Évaluation' : 'Assessment'}: ${training.evaluation}`, margin + 2, y);
  y += 8;

  // ── RNCP / Mandatory Mentions ──
  // Regulatory info without explicit "Mentions obligatoires" title
  checkPage(12);
  doc.setFontSize(7.5);
  doc.setTextColor(...slate);
  doc.setCharSpace(0);
  const mentions = [
    `${fr ? 'Code formation' : 'Training code'}: ${training.rncp.codeFormation}`,
    `${fr ? 'Code NSF' : 'NSF code'}: ${training.rncp.codeNSF}`,
    `Formacodes: ${training.rncp.formacodesPrincipaux.join(' · ')}`,
    `${fr ? 'Accessibilité' : 'Accessibility'}: ${training.rncp.accessibilite}`,
    `${fr ? 'Modalités d\'évaluation' : 'Assessment methods'}: ${training.rncp.modalitesEvaluation}`,
    `${fr ? 'Délai d\'accès' : 'Access time'}: ${training.rncp.delaiAcces}`,
    `${fr ? 'Taux de réussite' : 'Success rate'}: ${training.rncp.tauxReussite}`,
    `${fr ? 'Taux d\'insertion' : 'Employment rate'}: ${training.rncp.tauxInsertion}`,
    `Contact: ${training.rncp.contact}`,
  ];
  mentions.forEach(m => {
    checkPage(8);
    const lines = doc.splitTextToSize(m, contentWidth - 4);
    doc.text(lines, margin + 2, y);
    y += lines.length * 3.8 + 1;
  });
  y += 4;

  // ── Legal mentions / Gaius info ──
  checkPage(25);
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, y, pageWidth - margin, y);
  y += 5;
  doc.setFontSize(7);
  doc.setTextColor(...muted);
  doc.setCharSpace(0);
  doc.setFont('helvetica', 'bold');
  doc.text('Gaius — Formation à l\'IA juridique', margin, y);
  doc.setFont('helvetica', 'normal');
  y += 4;
  const legalLines = [
    'SAS au capital de 1 000 € — SIRET : 934 609 461 00018 — RCS Paris',
    'N° de déclaration d\'activité : en cours d\'enregistrement auprès de la DRIEETS Île-de-France',
    'Siège social : 229 rue Saint-Honoré, 75001 Paris',
    'Email : formation@gaius-tech.com — Site : gaius-tech.com',
    fr ? 'Certification Qualiopi — Catégorie : Actions de formation' : 'Qualiopi certification — Category: Training actions',
  ];
  legalLines.forEach(line => {
    doc.text(line, margin, y);
    y += 3.5;
  });
  y += 2;
  doc.setFontSize(6.5);
  doc.text(fr ? 'Document généré automatiquement — Programme susceptible de modifications' : 'Automatically generated — Program subject to change', margin, y);

  // Footer on last page
  addPageFooter();

  // Save
  const slug = training.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
  doc.save(`programme-${slug}.pdf`);
}
